import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { motion, AnimatePresence } from 'framer-motion'

const { FiArrowLeft, FiArrowRight, FiCheck, FiPlay, FiDownload, FiStar, FiTrendingUp } = FiIcons

const LessonView = () => {
  const { lessonId } = useParams()
  const { userProfile } = useAuth()
  const navigate = useNavigate()
  
  const [lesson, setLesson] = useState(null)
  const [progress, setProgress] = useState(null)
  const [loading, setLoading] = useState(true)
  const [marking, setMarking] = useState(false)
  const [showCelebration, setShowCelebration] = useState(false)
  const [dayLessons, setDayLessons] = useState([])

  useEffect(() => {
    if (lessonId && userProfile) {
      fetchLesson()
    }
  }, [lessonId, userProfile])

  const fetchLesson = async () => {
    try {
      setLoading(true)

      // Get lesson details
      const { data: lessonData, error: lessonError } = await supabase
        .from('lessons')
        .select('*')
        .eq('id', lessonId)
        .single()

      if (lessonError) throw lessonError

      setLesson(lessonData)

      // Get lesson progress
      const { data: progressData, error: progressError } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('lesson_id', lessonId)
        .eq('student_id', userProfile.user_id)
        .single()

      if (progressError && progressError.code !== 'PGRST116') {
        throw progressError
      }

      setProgress(progressData)

      // Get all lessons for this day for navigation
      if (lessonData) {
        const { data: dayLessonsData, error: dayError } = await supabase
          .from('lessons')
          .select('id, title, order_index')
          .eq('day_number', lessonData.day_number)
          .order('order_index')

        if (dayError) throw dayError
        setDayLessons(dayLessonsData || [])
      }

    } catch (error) {
      console.error('Error fetching lesson:', error)
    } finally {
      setLoading(false)
    }
  }

  const markComplete = async () => {
    try {
      setMarking(true)

      if (progress) {
        // Update existing progress
        const { error } = await supabase
          .from('lesson_progress')
          .update({
            completed: true,
            completed_at: new Date().toISOString()
          })
          .eq('id', progress.id)
      } else {
        // Create new progress record
        const { error } = await supabase
          .from('lesson_progress')
          .insert({
            lesson_id: lessonId,
            student_id: userProfile.user_id,
            completed: true,
            completed_at: new Date().toISOString()
          })
      }

      // Show celebration
      setShowCelebration(true)
      setTimeout(() => setShowCelebration(false), 3000)

      // Refresh lesson data
      await fetchLesson()

    } catch (error) {
      console.error('Error marking lesson complete:', error)
    } finally {
      setMarking(false)
    }
  }

  const navigateToLesson = (direction) => {
    const currentIndex = dayLessons.findIndex(l => l.id === lessonId)
    let targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1
    
    if (targetIndex >= 0 && targetIndex < dayLessons.length) {
      navigate(`/lesson/${dayLessons[targetIndex].id}`)
    }
  }

  const currentIndex = dayLessons.findIndex(l => l.id === lessonId)
  const canGoPrevious = currentIndex > 0
  const canGoNext = currentIndex < dayLessons.length - 1

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
          <div className="h-32 bg-gray-200 rounded" />
        </div>
      </div>
    )
  }

  if (!lesson) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Lesson not found</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Celebration Modal */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="bg-white rounded-2xl p-8 text-center max-w-md mx-4"
            >
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-[#1B4C87] mb-2">
                Great Job!
              </h3>
              <p className="text-gray-600">
                You've completed this lesson! Keep up the excellent work.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-gray-600 hover:text-[#00AAFF] transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateToLesson('previous')}
            disabled={!canGoPrevious}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          
          <span className="text-sm text-gray-600">
            Lesson {currentIndex + 1} of {dayLessons.length}
          </span>
          
          <button
            onClick={() => navigateToLesson('next')}
            disabled={!canGoNext}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </button>
        </div>
      </motion.div>

      {/* Lesson Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg p-8 mb-8"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-[#00AAFF]/10 text-[#00AAFF] text-sm rounded-full font-medium">
                Day {lesson.day_number}
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                {lesson.subject}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">{lesson.title}</h1>
            {lesson.description && (
              <p className="text-gray-600 text-lg">{lesson.description}</p>
            )}
          </div>
          
          {progress?.completed && (
            <div className="flex items-center text-green-600 bg-green-50 px-4 py-2 rounded-lg">
              <SafeIcon icon={FiStar} className="w-5 h-5 mr-2" />
              <span className="font-medium">Completed</span>
            </div>
          )}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Lesson Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-4">Lesson Content</h2>
            <div className="prose max-w-none">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(lesson.content || 'No content available for this lesson yet.')
                }}
                className="text-gray-700 leading-relaxed"
              />
            </div>
          </motion.div>

          {/* Video Section */}
          {lesson.video_url && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <SafeIcon icon={FiPlay} className="w-6 h-6 mr-2 text-[#00AAFF]" />
                Video Lesson
              </h2>
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <iframe
                  src={lesson.video_url}
                  className="w-full h-full rounded-lg"
                  allowFullScreen
                  title={lesson.title}
                />
              </div>
            </motion.div>
          )}

          {/* Attachments */}
          {lesson.attachments && lesson.attachments.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <SafeIcon icon={FiDownload} className="w-6 h-6 mr-2 text-[#00AAFF]" />
                Downloads & Worksheets
              </h2>
              <div className="space-y-3">
                {lesson.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-800">{attachment.name}</h3>
                      <p className="text-sm text-gray-600">{attachment.description}</p>
                    </div>
                    <button className="flex items-center text-[#00AAFF] hover:text-[#0099E5] transition-colors">
                      <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1" />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 mr-2 text-[#00AAFF]" />
              Progress
            </h3>
            
            {!progress?.completed ? (
              <button
                onClick={markComplete}
                disabled={marking}
                className="w-full bg-[#00AAFF] text-white py-3 rounded-lg font-medium hover:bg-[#0099E5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />
                {marking ? 'Marking Complete...' : 'Mark Complete'}
              </button>
            ) : (
              <div className="text-center py-4">
                <SafeIcon icon={FiStar} className="w-12 h-12 mx-auto text-green-500 mb-2" />
                <p className="text-green-600 font-medium">Lesson Completed!</p>
                <p className="text-sm text-gray-600 mt-1">
                  Completed on {new Date(progress.completed_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </motion.div>

          {/* Navigation Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4">Day {lesson.day_number} Lessons</h3>
            <div className="space-y-2">
              {dayLessons.map((dayLesson) => (
                <button
                  key={dayLesson.id}
                  onClick={() => navigate(`/lesson/${dayLesson.id}`)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    dayLesson.id === lessonId
                      ? 'bg-[#00AAFF] text-white'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="text-sm font-medium">{dayLesson.title}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Encouragement Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-[#00AAFF]/10 to-[#1B4C87]/10 rounded-xl p-6"
          >
            <h3 className="text-lg font-bold text-[#1B4C87] mb-2" style={{ fontFamily: 'Dancing Script' }}>
              You're Doing Great! 💪
            </h3>
            <p className="text-gray-700 text-sm">
              Every lesson completed is a step closer to mastering new skills. Keep up the fantastic work!
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default LessonView